import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Database } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { IconButton } from '../ui/IconButton';
import type { Channel } from '../../types';

const bare = (s: string) => s.trim().replace(/^@/, '').toLowerCase();

// One click from a channel to what feeds and shapes it: the Sources that write
// into it (where a mapping is most often corrected) and the Schemas it uses.
export function ChannelDataMenu({ channel }: { channel: Channel }) {
  const { mappings, templates, syncMappings, isMappingOwner } = useStore();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    void syncMappings();
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const me = bare(channel.nickname);
  const sources = (mappings ?? []).filter((m) => bare(String(m.spec?.channel ?? '')) === me && isMappingOwner(m));
  const ids = new Set(channel.templateIds ?? []);
  const schemas = (templates ?? []).filter((t) => ids.has(t.id) || ids.has(t.nickname));
  const row = 'block rounded-lg px-2.5 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/10 truncate';
  const head = 'px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-black/45 dark:text-white/45';

  return (
    <div ref={box} className="relative">
      <IconButton icon={Database} onClick={() => setOpen((v) => !v)} aria-label="Sources and schemas" aria-expanded={open} title="Sources and schemas" />
      {open && (
        <div role="menu" className="absolute right-0 top-10 z-30 w-72 max-h-[70vh] overflow-auto rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1b222b] p-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.18)]">
          <div className={head}>Sources</div>
          {sources.length === 0 && <div className="px-2.5 py-1.5 text-sm text-black/45 dark:text-white/45">None feed this channel.</div>}
          {sources.map((m) => (
            <Link key={m.id} role="menuitem" to={`/integrations/${m.id}`} className={row} onClick={() => setOpen(false)}>
              {m.title || m.nickname}
            </Link>
          ))}
          <div className={head}>Schemas</div>
          {schemas.length === 0 && <div className="px-2.5 py-1.5 text-sm text-black/45 dark:text-white/45">No schema attached.</div>}
          {schemas.map((t) => (
            <Link key={t.id} role="menuitem" to={`/templates/${t.id}/edit`} className={row} onClick={() => setOpen(false)}>
              {t.title || t.nickname}
            </Link>
          ))}
          <div className="mt-1 border-t border-black/[0.06] dark:border-white/[0.08] pt-1">
            <Link role="menuitem" to="/integrations" className={row} onClick={() => setOpen(false)}>All sources</Link>
          </div>
        </div>
      )}
    </div>
  );
}
